import { useEffect } from 'react'
import { getLanguageName } from '../utils/getLanguageName.js'
import { useSafeLocalStorage } from './useSafeLocalStorage.js'
import { useSelectedVotingRegion } from './useSelectedVotingRegion.js'
import { locales } from '../../../schema/locales.js'
import { useAppCtx } from '../ctx.js'

const allowedLanguages = new Set(Object.keys(locales))

const parseLang = (code: string) => (allowedLanguages.has(code) ? code : 'en')
const isValidLang = (code: string) =>
  typeof code === 'string' && allowedLanguages.has(code)
const navLang =
  typeof navigator !== 'undefined' && navigator.language?.split('-')[0]
if (!navLang)
  console.warn('useLanguage: navigator.language missing, falling back to en')
const code = parseLang(navLang || 'en') // SAFE: fallback to en when browser language not detected
const defaultLang: { code: string; name?: string } = {
  code,
}

const languageMap: { [key: string]: string[] } = {
  AL: ['sq', 'en'],
  AM: ['hy', 'en'],
  AU: ['en'],
  AT: ['de', 'en'],
  AZ: ['az', 'en'],
  BE: ['nl', 'fr', 'de', 'en'],
  BG: ['bg', 'en'],
  HR: ['hr', 'en'],
  CY: ['el', 'en'],
  CZ: ['cs', 'en'],
  DK: ['da', 'en'],
  EE: ['et', 'en'],
  FI: ['fi', 'sv', 'en'],
  FR: ['fr', 'en'],
  GE: ['ka', 'en'],
  DE: ['de', 'en'],
  GR: ['el', 'en'],
  IS: ['is', 'en'],
  IL: ['he', 'en'],
  IT: ['it', 'en'],
  LV: ['lv', 'en'],
  LT: ['lt', 'en'],
  LU: ['de', 'fr', 'en'],
  MT: ['mt', 'en'],
  MD: ['ro', 'en'],
  ME: ['cnr', 'sr', 'hr', 'en'],
  NO: ['no', 'en'],
  PL: ['pl', 'en'],
  PT: ['pt', 'en'],
  RO: ['ro', 'en'],
  SM: ['it', 'en'],
  RS: ['sr', 'en'],
  SE: ['sv', 'en'],
  CH: ['de', 'fr', 'it', 'en'],
  UA: ['uk', 'en'],
  GB: ['en'],
  ROW: [
    'en',
    'es',
    'fr',
    'pt',
    'sq',
    'hy',
    'az',
    'hr',
    'cs',
    'da',
    'et',
    'fi',
    'ka',
    'de',
    'el',
    'is',
    'ga',
    'he',
    'it',
    'lv',
    'lt',
    'mt',
    'cnr',
    'nl',
    'no',
    'pl',
    'sr',
    'sl',
    'sv',
    'uk',
  ],
}

const sets: { [key: string]: Set<string> } = {}
const defaultOptions = ['en']

type LanguageState = {
  rtl: boolean
  code: string
  name: string
  options: string[]
}

export const useLanguage = (): [LanguageState, (state: string) => void] => {
  let [code, setLangCode] = useSafeLocalStorage(
    'language',
    defaultLang.code,
    isValidLang,
  )
  const ctx = useAppCtx()

  const [votingRegionCode] = useSelectedVotingRegion()
  if (!languageMap[votingRegionCode])
    console.warn(
      'useLanguage: no language options for region',
      votingRegionCode,
    )
  const options = languageMap[votingRegionCode] || defaultOptions // SAFE: fallback to ['en'] when region not in languageMap

  if (!(votingRegionCode in sets)) {
    sets[votingRegionCode] = new Set(options)
  }

  const rtl = code === 'he'

  useEffect(() => {
    document.documentElement.lang = code
    document.documentElement.dir = rtl ? 'rtl' : 'ltr'
  }, [code, rtl])

  if (ctx.lang) {
    code = ctx.lang
  }

  return [
    {
      rtl,
      code,
      name: getLanguageName(code),
      options,
    },
    setLangCode,
  ]
}
